<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4 px-2">
      <d-col col sm="4" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('order.offersAndOrders') }}</span>
        <h3 class="page-title">
          {{ $t('order.orderStatus') }}
        </h3>
      </d-col>

      <d-col col sm="8" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          class="ml-2"
          @click.prevent="backToList">
          <i class="material-icons">arrow_back</i>
         {{ $t('ui.backToList') }}
        </d-button>

        <d-button
          v-if="!needsReturnToStock && allCollected && isOrderCollect"
          theme="success"
          :disabled="isAvailableToCollect || isCollectedByDifferentUser"
          class="ml-2"
          @click.prevent="submitFull">
          {{ $t('order.warehouse.collectedAll') }}
        </d-button>

        <d-button
          v-if="!needsReturnToStock && !allCollected && isOrderCollect"
          theme="warning"
          :disabled="isAvailableToCollect || isCollectedByDifferentUser"
          class="ml-2"
          @click.prevent="submitPartial">
          {{ $t('order.warehouse.collectedPartially') }}
        </d-button>

        <d-button
          v-if="!needsReturnToStock && !allCollected && isOrderCollect && hasPurchasedProducts"
          theme="danger"
          :outline="true"
          :disabled="isAvailableToCollect || isCollectedByDifferentUser"
          class="ml-2"
          @click.prevent="submitWaitingForProducts">
          {{ $t('order.warehouse.waitingForMerchandiseFromSupplier') }}
        </d-button>

        <d-button
          v-if="!needsReturnToStock && !allCollected && isOrderCollect"
          theme="danger"
          class="ml-2"
          :disabled="isAvailableToCollect || isCollectedByDifferentUser"
          @click.prevent="submitPartialWithEmptyStorage">
          {{ $t('order.warehouse.collectedPartiallyOutOfStock') }}
        </d-button>

        <d-button
          v-if="!needsReturnToStock && isOrderCheck"
          theme="danger"
          class="ml-2"
          @click.prevent="markOrderAsChecked">
          {{ $t('order.warehouse.iChecked') }}
        </d-button>

        <d-button
          v-if="needsReturnToStock"
          theme="danger"
          :disabled="isAvailableToCollect || isCollectedByDifferentUser"
          class="ml-2"
          @click.prevent="submitReturn">
          {{ $t('order.warehouse.returnToStock') }}
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <d-alert
        v-if="orderData.different_in_products && orderData.different_in_products.length"
        show
        theme="danger">
        {{ $t('order.warehouse.boxes.hasDifferentInProducts') }}
        <d-button
          theme="light"
          size="sm"
          class="ml-auto pt-1 pb-1"
          @click.prevent="scrollToSummary">
          {{ $t('order.warehouse.boxes.seeDetails') }}
        </d-button>
      </d-alert>

      <d-alert
        v-if="hasDeliveryNote"
        show
        theme="warning">
        {{ $t('order.warehouse.attachDeliveryNote') }}
      </d-alert>

      <d-alert
        v-if="isOrderCollect && isCollectedByDifferentUser"
        show
        theme="warning">
        {{ $t('order.warehouse.orderIsAlreadyBeeingCollectedByDifferentPerson') }}
      </d-alert>

      <d-alert
        v-if="orderData.collected_again"
        show
        theme="danger">
        {{ $t('order.warehouse.orderCollectingAgain') }}
      </d-alert>

      <d-alert
        v-if="orderData.blockade_status === 'BLOCKADE'"
        show
        theme="danger">
        {{ $t('order.warehouse.orderBlockedAtCustomerRequest') }}
      </d-alert>

      <d-alert
        v-if="needsReturnToStock"
        show
        theme="danger">
        {{ $t('order.warehouse.orderCancelledReturnToStock') }}
      </d-alert>

      <d-card
        v-if="!needsReturnToStock && !isOrderWaiting"
        class="p-3 mb-3">
        <d-col cols="12">
          {{ $t('order.download') }}:
          <d-button
            @click.prevent="downloadMarkingDoc(orderData.id)"
            :disabled="downloadingMarking"
            theme="secondary"
            class="ml-3">
            <template v-if="downloadingMarking">
              <div class="spinner-border spinner-border-sm" role="status"></div>
            </template>
            <i v-else class="material-icons">cloud_download</i>
            {{ $t('order.warehouse.mark') }}
          </d-button>

          <d-button
            v-if="hasDeliveryNote"
            @click.prevent="downloadDeliveryNote(orderData.id)"
            :disabled="downloadingDeliveryNote"
            theme="secondary"
            class="ml-3">
            <template v-if="downloadingDeliveryNote">
              <div class="spinner-border spinner-border-sm" role="status"></div>
            </template>
            <i v-else class="material-icons">cloud_download</i>
            {{ $t('order.warehouse.deliveryNote') }}
          </d-button>

          <d-button
            @click.prevent="downloadDeliveryNotes(orderData.id)"
            :disabled="downloadingNotes"
            theme="secondary"
            class="ml-3">
            <template v-if="downloadingNotes">
              <div class="spinner-border spinner-border-sm" role="status"></div>
            </template>
            <i v-else class="material-icons">cloud_download</i>
            {{ $t('order.warehouse.deliveryDocument') }}
          </d-button>
        </d-col>
      </d-card>

      <d-card
        v-if="isOrderCollect && !isCollectedByDifferentUser && isAvailableToCollect"
        class="mb-5">
        <d-card-body class="d-flex">
          <span v-if="!needsReturnToStock">{{ $t('order.warehouse.toStartCollectingOrderAcceptOrder') }}:</span>
          <span v-else>{{ $t('order.warehouse.toStartHandlingOrderReturnAcceptOrder') }}:</span>
          <d-button
            theme="success"
            :disabled="takeOrderInProgress"
            class="ml-auto"
            @click.prevent="takeOrder">
            <template v-if="!takeOrderInProgress">
              {{ $t('order.iAcceptOrder') }}
            </template>
            <template v-else>
              <div class="spinner-border spinner-border-sm" role="status"></div>
            </template>
          </d-button>
        </d-card-body>
      </d-card>

      <validation-observer ref="order-form" tag="form">
        <d-card class="mb-5">
          <table :class="{ 'table': true, 'is-locked': (isOrderCollect && (isAvailableToCollect || isCollectedByDifferentUser)) }">
            <thead>
              <tr>
                <th>{{ $t('product.product') }}</th>
                <th style="width: 100px;" class="text-center">{{ $t('ui.amount') }}</th>
                <th v-if="!needsReturnToStock" style="width: 200px;" class="text-center">{{ $t('container.containers') }}</th>
                <th v-if="!needsReturnToStock" style="width: 200px;" class="text-center">{{ $t('ui.order') }}</th>
                <th style="width: 350px">
                  <template v-if="needsReturnToStock">{{ $t('order.warehouse.returned') }}</template>
                  <template v-else>{{ $t('product.collected') }}</template>
                </th>
                <th style="width: 200px">{{ $t('status.status') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(product, index) of orderData.products"
                :key="'order-collect-row-' + index">
                <td class="align-middle">
                  {{ product.code }} - {{ product.name }}<br>
                  <small>
                    {{ $t('order.warehouse.warehouseAvailability') }}:
                    <span class="text-primary">
                      {{ getProductAvailability(product.product_id) }} {{ $t('ui.piecesAbbr') }}
                    </span>
                  </small>
                  <template v-if="product.purchase_product && product.purchase_product.size_name">
                    <br>
                    <small>
                      {{ $t('purchase.size.size') }}:
                      <span class="text-primary">
                        {{ product.purchase_product.size_name }}
                      </span>
                    </small>
                  </template>
                  <template v-if="product.purchase_product && product.purchase_product.color_name">
                    <br>
                    <small>
                      {{ $t('purchase.fabric.color') }}:
                      <span class="text-primary">
                        {{ product.purchase_product.color_name }}
                      </span>
                    </small>
                  </template>
                </td>
                <td class="align-middle text-center">
                  {{ product.quantity }} {{ $t('ui.piecesAbbr') }}
                </td>
                <td
                  v-if="!needsReturnToStock"
                  class="align-middle text-center">
                  <template v-if="product.containers && product.containers.length">
                    <div
                      v-for="(container, subindex) of product.containers"
                      :key="'product-' + index + '-container-' + subindex">
                      <small>
                        <span class="text-primary">{{ container.container_quantity }} {{ $t('ui.piecesAbbr') }}</span> - {{ container.container.date }}
                      </small>
                    </div>
                  </template>
                  <small v-else>&mdash;</small>
                </td>
                <td
                  v-if="!needsReturnToStock"
                  class="align-middle text-center">
                  <small v-if="product.purchase">
                    <span class="text-primary">{{ product.purchase_quantity }} {{ $t('ui.piecesAbbr') }}</span>
                    <br>
                    {{ product.purchase.customer_name }}
                  </small>
                  <small v-else>&mdash;</small>
                </td>
                <td class="align-middle">
                  <div class="d-flex">
                    <span style="width: 70px">
                      <template v-if="boxProductsCommentsCount && boxProductsCommentsCount[product.id]">
                        <i
                          class="material-icons text-danger mr-1 mt-1"
                          style="position: relative; top: 4px; cursor: pointer;"
                          slot="prepend"
                          @click="showCollectionComments(product.box_products)">comment</i>
                        <span class="mt-1 mr-2">
                          ({{ boxProductsCommentsCount[product.id] }})
                        </span>
                      </template>
                      <template v-else>
                        <i
                          class="material-icons text-gray mr-2 mt-1"
                          style="position: relative; top: 4px; opacity: .25"
                          slot="prepend">comment</i>
                      </template>
                    </span>

                    <validation-provider
                      :rules="'required|min_value:0|max_value:' + product.quantity"
                      name="number"
                      v-slot="{ errors }">
                      <d-input-group :prepend="needsReturnToStock ? $t('order.warehouse.returned') + ':' : $t('product.collected') + ':'" :append="$t('ui.piecesAbbr')">
                        <d-input
                          v-model="product.quantity_collected"
                          type="number"
                          @input="lastUpdatedChange"
                          :min="0"
                          :max="getMaximum(product.quantity, product.product_id)"
                          step="1"
                          :readonly="isOrderCheck || isOrderWaiting"
                          :state="errors.length ? 'invalid' : null"
                          :key="'order-collect-row-input-' + index"
                          style="width: 100px;"
                          class="text-right" />
                      </d-input-group>
                    </validation-provider>

                    <d-button
                      v-if="!(isOrderCheck || isOrderWaiting)"
                      theme="success"
                      class="ml-2"
                      :key="'order-collect-row-button-' + index"
                      @click.prevent="product.quantity_collected = product.quantity">
                      <i class="material-icons">check</i>
                    </d-button>
                  </div>
                </td>
                <td class="align-middle">
                  <d-badge
                    v-if="parseInt(product.quantity_collected, 10) === parseInt(product.quantity, 10) && parseInt(product.quantity_collected, 10) <= getMaximum(product.quantity, product.product_id)"
                    theme="success"
                    class="w-100"
                    :key="'status-collected-all-returned-whole-' + index">
                    <template v-if="needsReturnToStock">{{ $t('order.warehouse.returnedWhole') }}</template>
                    <template v-else>{{ $t('order.warehouse.collectedAll') }}</template>
                  </d-badge>
                  <d-badge
                    v-if="parseInt(product.quantity_collected, 10) < parseInt(product.quantity, 10) && parseInt(product.quantity_collected, 10) > 0 && parseInt(product.quantity_collected, 10) <= getMaximum(product.quantity, product.product_id)"
                    theme="warning"
                    class="w-100"
                    :key="'status-returned-collected-partially-' + index">
                    <template v-if="needsReturnToStock">{{ $t('order.warehouse.returnedPartially') }}</template>
                    <template v-else>{{ $t('order.warehouse.collectedPartially') }}</template>
                  </d-badge>
                  <d-badge
                    v-if="parseInt(product.quantity_collected, 10) < 0"
                    theme="danger"
                    outline
                    class="w-100"
                    :key="'status-incorrect-quantity-' + index">
                    {{ $t('order.warehouse.incorrectQuantity') }}
                  </d-badge>
                  <d-badge
                    v-if="parseInt(product.quantity_collected, 10) > getMaximum(product.quantity, product.product_id)"
                    theme="danger"
                    outline
                    class="w-100"
                    :key="'status-value-greater-than-current-stock-' + index"
                    v-pure-html="$t('order.warehouse.enteredValueIsGeaterThanCurrentStock', { valueQty: product.quantity, valueStock: getProductAvailability(product.product_id) })">
                  </d-badge>
                  <d-badge
                    v-if="parseInt(product.quantity_collected, 10) === 0"
                    theme="danger"
                    class="w-100"
                    :key="'status-not-collected-not-returned-' + index">
                    <template v-if="needsReturnToStock">{{ $t('order.warehouse.notReturned') }}</template>
                    <template v-else>{{ $t('order.warehouse.notCollected') }}</template>
                  </d-badge>
                </td>
              </tr>
            </tbody>
          </table>

          <div
            v-if="orderData && orderData.description_for_warehouse"
            class="form-group p-3">
            <label for="description">{{ $t('order.commentsToOrder') }}:</label>

            <p class="text-danger">
              {{ orderData.description_for_warehouse }}
            </p>
          </div>

          <div class="form-group p-3">
            <label>
              {{ $t('order.commentsRegardingOrder') }}:
            </label>

            <d-textarea
              v-model="orderData.warehouse_description"
              :rows="5"
              :readonly="isOrderWaiting"
              :value.prop="orderData.warehouse_description" />

            <d-button
              v-if="!isOrderWaiting"
              theme="success"
              :disabled="!!updateAdditionalInfoInProgress"
              class="ml-auto mt-2 mb-1"
              outline
              size="sm"
              @click.prevent="submitAdditionalInfo(orderData.id)">
              <template v-if="!updateAdditionalInfoInProgress">
                {{ $t('order.saveComments') }}
              </template>
              <template v-if="updateAdditionalInfoInProgress">
                <div class="spinner-border spinner-border-sm" role="status"></div>
              </template>
            </d-button>
          </div>
        </d-card>
      </validation-observer>
    </d-form>

    <order-collect-purchases
      :purchases="loadedPurchases" />

    <order-collect-boxes
      v-if="$showWarehouseBoxesUI"
      :deliveryNoteConfirmedAt="orderData.delivery_note_confirmed_at"
      :deliveryNoteConfirmedBy="orderData.delivery_note_confirmed_by"
      :orderID="orderData.id"
      :orderProducts="orderData.products"
      :boxesAcceptedAt="orderData.boxes_accepted_at" />

    <order-box-products-comments-popup
      v-if="displayBoxProductsCommentsPopup"
      :dataToDisplay="boxProductsComments" />

    <order-returns
      :returns="orderData.embroidery_returns" />
  </d-container>
</template>

<script>
import { mapGetters } from 'vuex';
import Vue from 'vue';
import FormUtils from './../../utils/FormUtils.js';
import OrderBoxProductsCommentsPopup from './OrderBoxProductsCommentsPopup.vue';
import OrderDifferentsPopup from './OrderDifferentsPopup.vue';
import OrderCollectBoxes from './OrderCollectBoxes.vue';
import OrderCollectPurchases from './OrderCollectPurchases.vue';
import OrderReturns from './OrderReturns.vue';

export default {
  name: 'order-collect',
  props: [
    'orderData'
  ],
  components: {
    'order-box-products-comments-popup': OrderBoxProductsCommentsPopup,
    'order-differents-popup': OrderDifferentsPopup,
    'order-collect-boxes': OrderCollectBoxes,
    'order-collect-purchases': OrderCollectPurchases,
    'order-returns': OrderReturns
  },
  computed: {
    ...mapGetters([
      'getUserID'
    ]),
    needsReturnToStock () {
      return this.orderData.blockade_status === 'RETURN_TO_STOCK';
    },
    isCollectedByDifferentUser () {
      return this.orderData.collect_user_id > 0 && this.orderData.collect_user_id !== this.getUserID;
    },
    isAvailableToCollect () {
      return this.orderData.collect_user_id === 0 && !this.isCollectedByCurrentUser;
    },
    allDataLoaded () {
      return this.loadedProducts && this.loadedContainers;
    },
    isOrderCollect () {
      return this.orderData.status === 'TO_COLLECT' || this.orderData.status === 'TO_DELETE' || this.orderData.status === 'DELETED';
    },
    isOrderWaiting () {
      return this.orderData.status === 'WAITING_PURCHASE_PRODUCTS';
    },
    isOrderCheck () {
      return this.orderData.status === 'TO_CHECK';
    },
    backUrl () {
      if (this.isOrderWaiting) {
        return '/oczekujace-na-dostawe';
      } else if (this.isOrderCheck) {
        return '/do-sprawdzenia';
      }

      return '/zamowienia';
    },
    hasPurchasedProducts () {
      for (let i = 0; i < this.orderData.products.length; i++) {
        if (this.orderData.products[i].containers && this.orderData.products[i].containers.length) {
          return true;
        }

        if (this.orderData.products[i].purchase_quantity) {
          return true;
        }
      }

      return false;
    },
    hasDeliveryNote () {
      return !!this.orderData.delivery_note_file;
    },
    boxProductsCommentsCount () {
      let commentsCount = {};

      for (let i = 0; i < this.orderData.products.length; i++) {
        let product = this.orderData.products[i];
        commentsCount[product.id] = 0;

        if (product.box_products) {
          let boxProductKeys = Object.keys(product.box_products);

          for (let j = 0; j < boxProductKeys.length; j++) {
            if (boxProductKeys[j] !== 'product_code') {
              let row = product.box_products[boxProductKeys[j]];

              if (row.collections && row.collections.length) {
                for (let k = 0; k < row.collections.length; k++) {
                  if (row.collections[k].description) {
                    commentsCount[product.id]++;
                  }
                }
              }

              if (row.verifications && row.verifications.length) {
                for (let l = 0; l < row.verifications.length; l++) {
                  if (row.verifications[l].description) {
                    commentsCount[product.id]++;
                  }
                }
              }

              if (row.returns && row.returns.length) {
                for (let l = 0; l < row.returns.length; l++) {
                  if (row.returns[l].description) {
                    commentsCount[product.id]++;
                  }
                }
              }
            }
          }
        }
      }

      return commentsCount;
    }
    /* ,
    allFieldsAreValid () {
      for (let i = 0; i < this.orderData.products.length; i++) {
        let product = this.orderData.products[i];

        if (product.quantity_collected < 0 || product.quantity_collected > this.getMaximum(product.quantity, product.product_id)) {
          return false;
        }
      }

      return true;
    }
    */
  },
  data () {
    return {
      containers: [],
      products: [],
      warehouse_description: false,
      allCollected: false,
      isCollectedByCurrentUser: false,
      loadedPurchases: this.orderData.purchases,
      // flags
      downloadingDeliveryNote: false,
      downloadingNotes: false,
      downloadingMarking: false,
      loadError: false,
      loadedContainers: false,
      loadedProducts: false,
      takeOrderInProgress: false,
      forceClosePurchaseInProgress: false,
      updateAdditionalInfoInProgress: false,
      forceClosePurchaseDone: false,
      displayBoxProductsCommentsPopup: false,
      boxProductsComments: null
    };
  },
  mounted () {
    this.loadAdditionalData();
    this.$bus.$on('order-creator-box-products-comments-popup-open', this.showBoxProductsCommentsPopup);
    this.$bus.$on('order-creator-box-products-comments-popup-close', this.closeBoxProductsCommentsPopup);
    this.$bus.$on('order-collect-purchases-reload-needed', this.reloadPurchases);
  },
  methods: {
    backToList () {
      localStorage.setItem('headwear-crm-warehouse-last-viewed', 'viewed;' + parseInt(this.orderData.id, 10) + ';' + +new Date());
      this.$router.push(this.backUrl);
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/products/all-items',
        loadedKey: 'loadedProducts',
        outputKey: 'products',
        errorKey: 'loadError',
        params: {
          where: {
            order_id: parseInt(this.$route.params.id, 10)
          }
        }
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/products/containers/items',
        loadedKey: 'loadedContainers',
        outputKey: 'containers',
        errorKey: 'loadError',
        params: {
          return: 'get'
        }
      });
    },
    lastUpdatedChange () {
      this.$forceUpdate();

      for (let i = 0; i < this.orderData.products.length; i++) {
        if (parseInt(this.orderData.products[i].quantity, 10) !== parseInt(this.orderData.products[i].quantity_collected, 10)) {
          this.allCollected = false;
          return;
        }
      }

      this.allCollected = true;
    },
    submitFull () {
      FormUtils.submit({
        $http: this.$http,
        endpoint: '/orders/update-collect/' + parseInt(this.orderData.id, 10),
        formData: {
          collect_type: 'FULL'
        },
        successTitle: this.$t('order.warehouse.submitCollectedSuccessTitle'),
        successText: this.$t('order.warehouse.submitCollectedSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('order.warehouse.submitErrorText'),
        successAction: () => {
          localStorage.setItem('headwear-crm-warehouse-last-viewed', 'saved;' + parseInt(this.orderData.id, 10) + ';' + +new Date());
          this.$router.push('/zamowienia/');
        }
      });
    },
    submitReturn () {
      let formDataToSend = {
        products: {}
      };

      for (let i = 0; i < this.orderData.products.length; i++) {
        let product = this.orderData.products[i];
        formDataToSend.products[product.id] = parseInt(product.quantity_collected, 10);
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/orders/update-return/' + parseInt(this.orderData.id, 10),
        formData: formDataToSend,
        successTitle: this.$t('order.warehouse.submitReturnSuccessTitle'),
        successText: this.$t('order.warehouse.submitReturnSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('order.warehouse.submitErrorText'),
        successAction: () => {
          localStorage.setItem('headwear-crm-warehouse-last-viewed', 'saved;' + parseInt(this.orderData.id, 10) + ';' + +new Date());
          this.$router.push('/zamowienia/');
        }
      });
    },
    submitPartial () {
      let formDataToSend = {
        collect_type: 'PARTIAL',
        products: {}
      };

      for (let i = 0; i < this.orderData.products.length; i++) {
        let product = this.orderData.products[i];
        formDataToSend.products[product.id] = parseInt(product.quantity_collected, 10);
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/orders/update-collect/' + parseInt(this.orderData.id, 10),
        formData: formDataToSend,
        successTitle: this.$t('order.warehouse.submitPartialSuccessTitle'),
        successText: this.$t('order.warehouse.submitPartialSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('order.warehouse.submitErrorText'),
        successAction: () => {
          localStorage.setItem('headwear-crm-warehouse-last-viewed', 'saved;' + parseInt(this.orderData.id, 10) + ';' + +new Date());
          this.$router.push('/zamowienia/');
        }
      });
    },
    submitWaitingForProducts () {
      let formDataToSend = {
        waiting_purchase_products: 1,
        collect_type: 'PARTIAL',
        products: {}
      };

      for (let i = 0; i < this.orderData.products.length; i++) {
        let product = this.orderData.products[i];
        formDataToSend.products[product.id] = parseInt(product.quantity_collected, 10);
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/orders/update-collect/' + parseInt(this.orderData.id, 10),
        formData: formDataToSend,
        successTitle: this.$t('order.warehouse.submitWaitingForProductsSccessTitle'),
        successText: this.$t('order.warehouse.submitWaitingForProductsSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('order.warehouse.submitErrorText'),
        successAction: () => {
          localStorage.setItem('headwear-crm-warehouse-last-viewed', 'saved;' + parseInt(this.orderData.id, 10) + ';' + +new Date());
          this.$router.push('/zamowienia/');
        }
      });
    },
    submitPartialWithEmptyStorage () {
      let formDataToSend = {
        collect_type: 'PARTIAL',
        products: {},
        add_order_status: true
      };

      for (let i = 0; i < this.orderData.products.length; i++) {
        let product = this.orderData.products[i];
        formDataToSend.products[product.id] = parseInt(product.quantity_collected, 10);
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/orders/update-collect/' + parseInt(this.orderData.id, 10),
        formData: formDataToSend,
        successTitle: this.$t('order.warehouse.submitPartialWithEmptyStorageSuccessTitle'),
        successText: this.$t('order.warehouse.submitPartialWithEmptyStorageSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('order.warehouse.submitErrorText'),
        successAction: () => {
          localStorage.setItem('headwear-crm-warehouse-last-viewed', 'saved;' + parseInt(this.orderData.id, 10) + ';' + +new Date());
          this.$router.push('/zamowienia/');
        }
      });
    },
    submitAdditionalInfo () {
      this.updateAdditionalInfoInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/orders/update-by-warehouse/' + this.orderData.id,
        formData: {
          warehouse_description: this.orderData.warehouse_description
        },
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.actionFailedTryAgain'),
        successAction: () => {
          this.updateAdditionalInfoInProgress = false;
          Vue.swal({
            title: this.$t('order.commentsSaved'),
            html: '',
            type: 'success',
            timer: 2000,
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        },
        failAction: () => {
          this.updateAdditionalInfoInProgress = false;
        }
      });
    },
    markOrderAsChecked () {
      FormUtils.submit({
        $http: this.$http,
        endpoint: '/orders/statuses/change-status/' + this.orderData.id,
        formData: {
          status: 'PREPARATION_FOR_SHIPPING'
        },
        successTitle: this.$t('status.changeStatusSuccessTitle'),
        successText: this.$t('status.changeStatusSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('status.changeStatusErrorText'),
        successAction: () => {
          this.$router.push('/do-sprawdzenia');
        }
      });
    },
    takeOrder () {
      this.takeOrderInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/orders/take-to-collect/' + this.orderData.id,
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('order.orderCollectErrorText'),
        successAction: () => {
          this.takeOrderInProgress = false;
          this.isCollectedByCurrentUser = this.getUserID;
        },
        failAction: () => {
          this.takeOrderInProgress = false;
        }
      });
    },
    getContainerInfo (containerID) {
      let foundedContainer = this.containers.filter(container => container.id === containerID);

      if (foundedContainer.length) {
        return foundedContainer[0].number + ' (dostawa: ' + foundedContainer[0].date + ')';
      }

      return '';
    },
    downloadMarkingDoc (orderID) {
      this.downloadingMarking = true;

      this.$http.post('/orders/box-marking/' + orderID, {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', this.$t('order.markingFileNamePrefix') + orderID + '.pdf');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingMarking = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingMarking = false;
      });
    },
    downloadDeliveryNote (orderID) {
      this.downloadingDeliveryNote = true;

      this.$http.post('/orders/download-order-delivery-note/' + orderID, {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        document.body.appendChild(link);
        let filename = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        link.setAttribute('download', filename.replace(/"/gmi, ''));
        link.click();
        document.body.removeChild(link);
        this.downloadingDeliveryNote = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingDeliveryNote = false;
      });
    },
    downloadDeliveryNotes (orderID) {
      this.downloadingNotes = true;

      this.$http.post('/orders/delivery-notes/' + orderID, {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', this.$t('order.deliveryNotesFileNamePrefix') + orderID + '.pdf');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingNotes = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingNotes = false;
      });
    },
    getProductAvailability (productID) {
      let foundedProduct = this.products.filter(product => product.id === productID);

      if (foundedProduct && foundedProduct[0]) {
        return parseInt(foundedProduct[0].availability, 10);
      }

      return '??';
    },
    getMaximum(quantity, productID) {
      let availability = this.getProductAvailability(productID);

      if (quantity > availability) {
        return availability;
      }

      return parseInt(quantity, 10);
    },
    forceClosePurchase () {
      this.forceClosePurchaseInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/purchases/force-close/' + this.orderData.purchase.id,
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('order.warehouse.receiveFromSupplierErrorText'),
        successAction: () => {
          this.forceClosePurchaseInProgress = false;

          Vue.swal({
            title: this.$t('order.warehouse.receiveFromSupplierSuccessTitle'),
            html: this.$t('order.warehouse.receiveFromSupplierSuccessText'),
            type: 'success',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });

          this.forceClosePurchaseDone = true;
        },
        failAction: () => {
          this.forceClosePurchaseInProgress = false;
        }
      });
    },
    showCollectionComments (boxProducts) {
      this.$bus.$emit('order-creator-box-products-comments-popup-open', boxProducts);
    },
    showBoxProductsCommentsPopup (dataToDisplay) {
      this.boxProductsComments = dataToDisplay;
      this.displayBoxProductsCommentsPopup = true;
    },
    closeBoxProductsCommentsPopup () {
      this.boxProductsComments = null;
      this.displayBoxProductsCommentsPopup = false;
    },
    scrollToSummary () {
      if (document.getElementById('boxes-summary')) {
        document.getElementById('boxes-summary').scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center'
        });
      }
    },
    reloadPurchases () {
      this.$bus.$emit('order-collect-loading-purchases', true);

      FormUtils.submitInBackground({
        $http: this.$http,
        method: 'get',
        endpoint: '/orders/item/' + this.$route.params.id,
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.errorOccuredDesc'),
        formData: {},
        successAction: (response) => {
          Vue.set(this, 'loadedPurchases', response.data.purchases);
          this.$bus.$emit('order-collect-loading-purchases', false);
        },
        failAction: () => {
          this.$bus.$emit('order-collect-loading-purchases', false);
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('order-creator-box-products-comments-popup-open', this.showBoxProductsCommentsPopup);
    this.$bus.$off('order-creator-box-products-comments-popup-close', this.closeBoxProductsCommentsPopup);
    this.$bus.$off('order-collect-purchases-reload-needed', this.reloadPurchases);
  }
};
</script>

<style lang="scss" scoped>
.form-control.is-select {
  background-image: none!important;
  padding: 0!important;
}

.text-muted {
  font-weight: 300;
}

.table.is-locked {
  opacity: 0.4;
  pointer-events: none;
}
</style>
